/* eslint react-hooks/exhaustive-deps: "warn" */
import { type FormEvent, type MouseEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './DataLicensingRedistributionClose.module.scss';

type Translate = (key: string) => string;

const getSteps = (t: Translate) => [
  {
    num: '01',
    text: t('You paste the clause, or describe the restriction in your own words.'),
  },
  {
    num: '02',
    text: t('Our licensing desk answers in writing within two business days, in plain language.'),
  },
  {
    num: '03',
    text: t('If it works, a scoped sample lands in your schema before anything is signed.'),
  },
];

export const DataLicensingRedistributionClose = () => {
  const { t } = useInt();
  const steps = getSteps(t);
  const rootRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [clause, setClause] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  const canSubmit = email.trim().length > 0 && clause.trim().length > 0 && !submitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      return;
    }

    setSubmitting(true);

    setTimeout(() => {
      setSubmitting(false);
      setSent(true);
    }, 700);
  };

  return (
    <div id="clause">
      <Section background="#080D19" className={styles.root}>
        <div className={styles.glow} aria-hidden="true" />

        <div ref={rootRef} className={cn(styles.grid, styles.reveal, revealed && styles.revealed)}>
          <div className={styles.copy}>
            <TitleBlock
              theme="dark"
              align="left"
              title={
                <>
                  {t('Send us the clause')}
                  <br />
                  {t('that is blocking you')}
                </>
              }
              description={t(
                'Most licensing conversations start with a redistribution restriction, a territory limit, or a deletion obligation someone found in week three of legal review. Send the clause. We tell you directly whether we can grant it.',
              )}
            />

            <div className={styles.steps}>
              {steps.map((step) => (
                <div key={step.num} className={styles.step}>
                  <span className={styles.stepNum}>{step.num}</span>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card} onMouseMove={handleSpotlight}>
            <span className={styles.spotlight} aria-hidden="true" />
            <span className={cn(styles.corner, styles.cornerTl)} aria-hidden="true" />
            <span className={cn(styles.corner, styles.cornerBr)} aria-hidden="true" />
            <span className={styles.slaBadge}>
              <span className={styles.slaDot} aria-hidden="true" />
              {t('Written answer in 2 business days')}
            </span>

            {!sent ? (
              <form onSubmit={handleSubmit}>
                <div className={styles.formTitle}>{t('Paste the clause')}</div>
                <p className={styles.formSub}>{t('Goes to the licensing desk, not to a sales queue.')}</p>

                <div className={styles.field}>
                  <label htmlFor="dlr-email">{t('Work email')}</label>
                  <input
                    id="dlr-email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label htmlFor="dlr-company">{t('Company')}</label>
                  <input
                    id="dlr-company"
                    type="text"
                    placeholder={t('Company name')}
                    autoComplete="organization"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label htmlFor="dlr-clause">{t('The clause, or the restriction')}</label>
                  <textarea
                    id="dlr-clause"
                    placeholder={t('“Licensee may use the Data solely for its internal business purposes...”')}
                    value={clause}
                    onChange={(e) => setClause(e.target.value)}
                  />
                </div>

                <button type="submit" className={styles.submit} disabled={!canSubmit}>
                  {submitting ? t('Sending…') : t('Send it over')}
                  {!submitting && (
                    <span className={styles.submitArrow} aria-hidden="true">
                      →
                    </span>
                  )}
                </button>

                <p className={styles.fine}>
                  {t('A named person at the licensing desk replies. No sequence, no drip.')}
                </p>
              </form>
            ) : (
              <div className={styles.done}>
                <span className={styles.doneCheck} aria-hidden="true">
                  ✓
                </span>
                <div className={styles.doneTitle}>{t('Received.')}</div>
                <p>
                  {t(
                    'The licensing desk has it. You will get a written answer within two business days, from a person, with the specific grant language attached.',
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
};
